/* moonbundle-i18n.js
   i18n for Moon Bundle
   - Translates UI/product strings (sv/en/de/no/fi)
   - Boundary-safe replacements (no mid-word issues)
   - Word-level mapping: paket<->bundle (Paket/Pakke/Paketti)
   - SAVE badge: sv/SEK keeps "Du sparar 2 500 kr"; non-SEK shows "~ <local currency>"
   - Heading "Paketera & spara" → always UPPERCASE by locale
   - NEW: Replace "Ordinarie pris / Regular price / ..." with "Utan kamin / Without stove / ..." in the widget
   - Debounced MutationObserver for re-renders
*/

(function () {
  // ========== Locale / currency helpers ==========
  function getLocale() {
    const langAttr = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (langAttr) return langAttr.split('-')[0];
    const m = location.pathname.match(/^\/([a-z]{2})(\/|$)/i);
    if (m) return m[1].toLowerCase();
    return 'en';
  }

  function getActiveCurrency() {
    if (window.Shopify && Shopify.currency && Shopify.currency.active) {
      return String(Shopify.currency.active).toUpperCase();
    }
    const fallbackByLocale = { sv:'SEK', no:'NOK', fi:'EUR', de:'EUR', en:'USD' };
    return fallbackByLocale[getLocale()] || 'SEK';
  }

  // FX from SEK -> target (update if needed)
  const FX_FROM_SEK = { SEK:1, EUR:0.088, USD:0.094, NOK:0.98, DKK:0.65, GBP:0.074 };
  const CUR_SYMBOL  = { SEK:'kr', EUR:'€', USD:'$', NOK:'kr', DKK:'kr', GBP:'£' };

function formatMoney(num, cur) {
  // For savings display: always round to nearest whole unit
  const opts = { maximumFractionDigits: 0 };
  try { 
    return new Intl.NumberFormat(undefined, opts).format(Math.round(num)); 
  }
  catch { 
    return String(Math.round(num)); 
  }
}

  // ========== SAVE badge (label + currency conversion) ==========
  // Only translates the label; for non-SEK markets shows "~ <local currency>"
  function translateSaveLabel(text, locale) {
    if (/\~\s*[€$£]|~\s*kr/i.test(text)) return text; // already converted

    const LABELS = {
      long: { sv:'Du sparar', en:'You save', de:'Sie sparen', no:'Du sparer', fi:'Säästät' },
      short:{ sv:'Spara',     en:'Save',      de:'Sparen',    no:'Spar',      fi:'Säästä' }
    };

    const re = /(Du sparar|Spara|You save|Save|Sie sparen|Du sparer|Säästät)\s*([\d\s.,\u00A0\u202F]+)(?:\s*(kr|SEK))?/gi;

    return text.replace(re, (m, label, raw) => {
      const isShort   = /^spara$|^save$/i.test(label);
      const baseLabel = (isShort ? LABELS.short[locale] : LABELS.long[locale]) || label;

      // Check if it's a percentage (contains %)
      if (raw.includes('%')) {
        return `${baseLabel} ${raw}`;
      }

      // Check if the raw value looks like a percentage (small numbers like 20, 30, etc.)
      const digitsOnly = (raw.match(/\d+/g) || []).join('');
      const amountSEK  = digitsOnly ? Number(digitsOnly) : 0;
      
      // If it's a small number (likely percentage), don't add currency
      if (amountSEK > 0 && amountSEK <= 100) {
        return `${baseLabel} ${raw}`;
      }

      const activeCur = getActiveCurrency();

      if (activeCur === 'SEK') {
        const sekFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(amountSEK);
        return `${baseLabel} ${sekFmt} kr`;
      } else {
        const rate = FX_FROM_SEK[activeCur] || 1;
        const converted = amountSEK * rate;
        const sym = CUR_SYMBOL[activeCur] || activeCur;
        const fmt = formatMoney(converted, activeCur);
        return `${baseLabel} ~ ${sym}${sym === 'kr' ? ' ' : ''}${fmt}`;
      }
    });
  }

  // ========== Boundary-safe replacement helpers ==========
  function esc(str){ return str.replace(/[.*+?^${}()|[\]\\]/g,'\\$&'); }
  function replaceWhole(text, from, to){
    const re = new RegExp(`(?<![\\p{L}])${esc(from)}(?![\\p{L}])`, 'giu');
    return text.replace(re, to);
  }
  function applyMapBoundarySafe(text, map){
    const entries = Array.from(map.entries()).sort((a,b)=>b[0].length - a[0].length);
    let out = text;
    for (const [from, to] of entries){
      out = replaceWhole(out, from, to);
    }
    return out;
  }

  // ========== Translate standalone words "paket" and "bundle" ==========
  function translateWordPaket(text, locale) {
    const words = {
      sv:{lower:'paket',   cap:'Paket',   upper:'PAKET'  },
      en:{lower:'bundle',  cap:'Bundle',  upper:'BUNDLE' },
      de:{lower:'paket',   cap:'Paket',   upper:'PAKET'  },
      no:{lower:'pakke',   cap:'Pakke',   upper:'PAKKE'  },
      fi:{lower:'paketti', cap:'Paketti', upper:'PAKETTI'}
    }[locale] || { lower:'bundle', cap:'Bundle', upper:'BUNDLE' };

    let out = text;
    out = replaceWhole(out, 'paket', words.lower);
    out = replaceWhole(out, 'Paket', words.cap);
    out = replaceWhole(out, 'PAKET', words.upper);
    return out;
  }

  function translateWordBundle(text, locale) {
    const words = {
      sv:{lower:'paket',   cap:'Paket',   upper:'PAKET'  },
      en:{lower:'bundle',  cap:'Bundle',  upper:'BUNDLE' }, // keep EN
      de:{lower:'paket',   cap:'Paket',   upper:'PAKET'  },
      no:{lower:'pakke',   cap:'Pakke',   upper:'PAKKE'  },
      fi:{lower:'paketti', cap:'Paketti', upper:'PAKETTI'}
    }[locale] || { lower:'bundle', cap:'Bundle', upper:'BUNDLE' };

    let out = text;
    out = replaceWhole(out, 'bundle', words.lower);
    out = replaceWhole(out, 'Bundle', words.cap);
    out = replaceWhole(out, 'BUNDLE', words.upper);
    return out;
  }

  // ========== Force heading uppercase ==========
  function fixHeadingUppercase(text, locale) {
    const patterns = [
      'Paketera & spara','Paketera & Spara','PAKETERA & SPARA','Paketera & SPARA','PAKETERA & Spara',
      'Bundla & spara'
    ];
    const targets = {
      sv: 'PAKETERA & SPARA',
      en: 'BUNDLE & SAVE',
      de: 'BÜNDELN & SPAREN',
      no: 'PAKK OG SPAR',
      fi: 'PAKETOI & SÄÄSTÄ'
    };
    const repl = targets[locale] || targets['en'];
    let out = text;
    patterns.forEach(p => { out = out.replace(new RegExp(esc(p),'gi'), repl); });
    return out;
  }

  

  // ========== Phrase map ==========
  const PHRASES = {
    // UI
    'Add to bundle': { sv:'Lägg till i paketet', en:'Add to bundle', de:'Zum Paket hinzufügen', no:'Legg til i pakke', fi:'Lisää pakettiin' },
    'Bundle':        { sv:'Paket', en:'Bundle', de:'Paket', no:'Pakke', fi:'Paketti' },
    'Bundle price':  { sv:'Paketpris', en:'Bundle price', de:'Paketpreis', no:'Pakkens pris', fi:'Paketin hinta' },
    'You save':      { sv:'Du sparar', en:'You save', de:'Sie sparen', no:'Du sparer', fi:'Säästät' },
    'Configure bundle': { sv:'Konfigurera paket', en:'Configure bundle', de:'Paket konfigurieren', no:'Konfigurer pakke', fi:'Määritä paketti' },
    'Included items':   { sv:'Ingår', en:'Included items', de:'Enthaltene Artikel', no:'Inkluderte produkter', fi:'Sisältyvät tuotteet' },
    'Remove': { sv:'Ta bort', en:'Remove', de:'Entfernen', no:'Fjern', fi:'Poista' },
    'Add':    { sv:'Lägg till', en:'Add', de:'Hinzufügen', no:'Legg til', fi:'Lisää' },
    'View details': { sv:'Visa detaljer', en:'View details', de:'Details anzeigen', no:'Se detaljer', fi:'Näytä tiedot' },
    'Select': { sv:'Välj', en:'Select', de:'Auswählen', no:'Velg', fi:'Valitse' },
    'Regular price': { sv:'Ordinarie pris', en:'Regular price', de:'Regulärer Preis', no:'Ordinær pris', fi:'Normaalihinta' }, // kept for other contexts

    // Badges / headings
    'Special offer': { sv:'Erbjudande', en:'Special offer', de:'Angebot', no:'Tilbud', fi:'Tarjous' },
    'SPECIAL OFFER': { sv:'ERBJUDANDE', en:'SPECIAL OFFER', de:'ANGEBOT', no:'TILBUD', fi:'TARJOUS' },
    'Fri frakt': { sv:'Fri frakt', en:'Free shipping', de:'Kostenloser Versand', no:'Gratis frakt', fi:'Ilmainen toimitus' },
    'MEST VÄRDE': { sv:'MEST VÄRDE', en:'BEST VALUE', de:'BESTER WERT', no:'MEST VERDI', fi:'PARAS ARVO' },
    'MEST POPULÄR': { sv:'MEST POPULÄR', en:'MOST POPULAR', de:'AM BELIEBTESTEN', no:'MEST POPULÆR', fi:'SUOSITUIN' },

    // Section heading
    'Paketera & spara': { sv:'Paketera & spara', en:'Bundle & save', de:'Bündeln & sparen', no:'Pakk og spar', fi:'Paketoi & säästä' },
    'Paketera & Spara': { sv:'Paketera & Spara', en:'Bundle & Save', de:'Bündeln & Sparen', no:'Pakk og Spar', fi:'Paketoi & Säästä' },
    'PAKETERA & SPARA': { sv:'PAKETERA & SPARA', en:'BUNDLE & SAVE', de:'BÜNDELN & SPAREN', no:'PAKK OG SPAR', fi:'PAKETOI & SÄÄSTÄ' },
    'Paketera & SPARA': { sv:'Paketera & SPARA', en:'BUNDLE & SAVE', de:'BÜNDELN & SPAREN', no:'PAKK OG SPAR', fi:'PAKETOI & SÄÄSTÄ' },
    'PAKETERA & Spara': { sv:'PAKETERA & Spara', en:'BUNDLE & SAVE', de:'BÜNDELN & SPAREN', no:'PAKK OG SPAR', fi:'PAKETOI & SÄÄSTÄ' },

    // Bundle names (localized trailing word)
    'Bara tält':  { sv:'Bara tält', en:'Tent only', de:'Nur Zelt', no:'Kun telt', fi:'Pelkkä teltta' },
    'BASE PAKET': { sv:'BASE PAKET', en:'BASE BUNDLE', de:'BASIS-PAKET', no:'BASE-PAKKE', fi:'BASE-PAKETTI' },
    'PLUS PAKET': { sv:'PLUS PAKET', en:'PLUS BUNDLE',  de:'PLUS-PAKET',  no:'PLUS-PAKKE',  fi:'PLUS-PAKETTI' },
    'PRO PAKET':  { sv:'PRO PAKET',  en:'PRO BUNDLE',   de:'PROFI-PAKET', no:'PRO-PAKKE',   fi:'PRO-PAKETTI' },
    'MAX PAKET':  { sv:'MAX PAKET',  en:'MAX BUNDLE',   de:'MAX-PAKET',   no:'MAX-PAKKE',   fi:'MAXI-PAKETTI' },

      // Regular price
  'Ordinarie pris': { 
    sv:'Ordinarie pris', 
    en:'Regular price', 
    de:'Regulärer Preis', 
    no:'Ordinær pris', 
    fi:'Normaalihinta' 
  },

  // Without stove
  'Utan kamin': { 
    sv:'Utan kamin', 
    en:'Without stove', 
    de:'Ohne Ofen', 
    no:'Uten ovn', 
    fi:'Ilman kiuasta' 
  },

    // Back in stock / notify texts
  'Få besked när varan finns att köpa': {
    sv:'Få besked när varan finns att köpa',
    en:'Get notified when the item is available',
    de:'Benachrichtigt werden, wenn der Artikel verfügbar ist',
    no:'Få beskjed når varen er tilgjengelig',
    fi:'Saat ilmoituksen, kun tuote on ostettavissa'
  },
  'Vi skickar ett mail till dig när varan åter finns i lager.': {
    sv:'Vi skickar ett mail till dig när varan åter finns i lager.',
    en:'We will send you an email when the item is back in stock.',
    de:'Wir senden Ihnen eine E-Mail, wenn der Artikel wieder auf Lager ist.',
    no:'Vi sender deg en e-post når varen er tilbake på lager.',
    fi:'Lähetämme sinulle sähköpostin, kun tuote on taas varastossa.'
  },
  'E-postadress': {
    sv:'E-postadress',
    en:'Email address',
    de:'E-Mail-Adresse',
    no:'E-postadresse',
    fi:'Sähköpostiosoite'
  },
  'Skicka': {
    sv:'Skicka',
    en:'Send',
    de:'Senden',
    no:'Send',
    fi:'Lähetä'
  },
  'Vi respekterar din integritet och delar inte din e-postadress med någon.': {
    sv:'Vi respekterar din integritet och delar inte din e-postadress med någon.',
    en:'We respect your privacy and will not share your email address with anyone.',
    de:'Wir respektieren Ihre Privatsphäre und geben Ihre E-Mail-Adresse nicht weiter.',
    no:'Vi respekterer ditt personvern og deler ikke e-postadressen din med noen.',
    fi:'Kunnioitamme yksityisyyttäsi emmekä jaa sähköpostiosoitettasi kenellekään.'
  },
  '🔔 Meddela mig': {
    sv:'🔔 Meddela mig',
    en:'🔔 Notify me',
    de:'🔔 Benachrichtige mich',
    no:'🔔 Varsle meg',
    fi:'🔔 Ilmoita minulle'
  },
  'när produkten går att köpa': {
    sv:'när produkten går att köpa',
    en:'when the product becomes available',
    de:'wenn das Produkt verfügbar ist',
    no:'når produktet kan kjøpes',
    fi:'kun tuote on ostettavissa'
  },

    // Bundle contents
    'Bastutält Flamman inkl. bärväska, markspik, stormlinor, golv med flammsäker matta.': {
      sv:'Bastutält Flamman inkl. bärväska, markspik, stormlinor, golv med flammsäker matta.',
      en:'Sauna tent Flamman incl. carrying bag, ground pegs, storm lines, floor with fireproof mat.',
      de:'Saunazelt Flamman inkl. Tragetasche, Heringe, Sturmleinen, Boden mit feuerfester Matte.',
      no:'Badstutelt Flamman inkl. bæreveske, plugger, stormliner, gulv med brannsikker matte.',
      fi:'Saunateltta Flamman sis. kantokassin, maakiilat, myrskynarut, lattian paloturvallisella matolla.'
    },
    'Bastutält Gnistan inkl. bärväska, tältpinnar, stormlinor, golv med flammsäker matta.': {
      sv:'Bastutält Gnistan inkl. bärväska, tältpinnar, stormlinor, golv med flammsäker matta.',
      en:'Sauna tent Gnistan incl. carrying bag, tent pegs, storm lines, floor with fireproof mat.',
      de:'Saunazelt Gnistan inkl. Tragetasche, Zeltpflöcke, Sturmleinen, Boden mit feuerfester Matte.',
      no:'Badstutelt Gnistan inkl. bæreveske, teltplugger, stormliner, gulv med brannsikker matte.',
      fi:'Saunateltta Gnistan sis. kantokassin, telttakiilat, myrskynarut, lattian paloturvallisella matolla.'
    },
    'Bastukamin ❘ The Beast ❘ Inkl. bärväska': {
      sv:'Bastukamin | The Beast | Inkl. bärväska',
      en:'Sauna stove | The Beast | Incl. carrying bag',
      de:'Saunaofen | The Beast | inkl. Tragetasche',
      no:'Badstuovn | The Beast | inkl. bæreveske',
      fi:'Saunankiuas | The Beast | sis. kantokassin'
    },
    'Användningsguide + lagningskit': { sv:'Användningsguide + lagningskit', en:'User guide + repair kit', de:'Bedienungsanleitung + Reparaturset', no:'Bruksanvisning + reparasjonssett', fi:'Käyttöohje + korjaussarja' },
    'Bastusten 20kg': { sv:'Bastusten 20kg', en:'Sauna stones 20kg', de:'Saunasteine 20kg', no:'Badstusteiner 20kg', fi:'Saunakivet 20kg' },
    'Bastubänk': { sv:'Bastubänk', en:'Sauna bench', de:'Saunabank', no:'Badstubenk', fi:'Saunapenkki' },
    'Hink och skopa': { sv:'Hink och skopa', en:'Bucket and ladle', de:'Eimer und Schöpfer', no:'Bøtte och øse', fi:'Sanko ja kauha' },
    'Bastutermometer': { sv:'Bastutermometer', en:'Sauna thermometer', de:'Saunathermometer', no:'Badstutermometer', fi:'Saunamittari' },
    'Värmetåliga läderhandskar': { sv:'Värmetåliga läderhandskar', en:'Heat-resistant leather gloves', de:'Hitzeresistente Lederhandschuhe', no:'Varmeresistente hansker i skinn', fi:'Lämmönkestävät nahkahanskat' },
    'Regnskydd': { sv:'Regnskydd', en:'Rain cover', de:'Regenschutz', no:'Regntrekk', fi:'Sadesuoja' },
    'Flame Gun - Eldstartare': { sv:'Flame Gun - Eldstartare', en:'Flame gun - Fire starter', de:'Flammenwerfer - Anzünder', no:'Flame gun - Tennstarter', fi:'Flame gun - Sytytin' },
    'Bärväska för ved': { sv:'Bärväska för ved', en:'Firewood carrying bag', de:'Tragetasche för Brennholz', no:'Bæreveske for ved', fi:'Polttopuiden kantolaukku' },

    // Water/shower
    'Water tank only':    { sv:'Bara vattentank', en:'Water tank only', de:'Nur Wassertank', no:'Kun vanntank', fi:'Pelkkä vesisäiliö' },
    'Water tank + Shower':{ sv:'Vattentank + Dusch', en:'Water tank + Shower', de:'Wassertank + Dusche', no:'Vanntank + Dusj', fi:'Vesisäiliö + Suihku' },
    'Vattentank 5 liter': { sv:'Vattentank 5 liter', en:'Water tank 5 liters', de:'Wassertank 5 Liter', no:'Vanntank 5 liter', fi:'Vesisäiliö 5 litraa' },
    'Portabel dusch med Hink': { sv:'Portabel dusch med Hink', en:'Portable shower with bucket', de:'Tragbare Dusche mit Eimer', no:'Bærbar dusj med bøtte', fi:'Kannettava suihku ämpärillä' },
    
    // Button texts
    'FÖRBESTÄLL': { sv:'FÖRBESTÄLL', en:'PRE-ORDER', de:'VORBESTELLEN', no:'FORHÅNDSBESTILL', fi:'ENNAKKOTILAUS' },
    'FÖRBESTÄLL DIN BASTU IDAG': { sv:'FÖRBESTÄLL DIN BASTU IDAG', en:'PRE-ORDER YOUR SAUNA TODAY', de:'BESTELLEN SIE IHR SAUNA HEUTE VOR', no:'FORHÅNDSBESTILL DIN BASTU I DAG', fi:'TEE ENNAKKOTILAUS SAUNASTASI TÄNÄÄN' }
  };

  function getTargetMap(locale) {
    const direct = new Map();
    Object.keys(PHRASES).forEach(key => {
      const t = PHRASES[key][locale] || key;
      direct.set(key, t);
      Object.values(PHRASES[key]).forEach(val => direct.set(val, t));
    });
    return direct;
  }

  // ========== Translators ==========
  const ROOTS = ['#moonbundle-root','.moonbundle','[data-moonbundle]','[id*="moonbundle"]','.moonbundleCustomLoader'];
  const ATTRS = ['title','aria-label'];

  function translateTextNode(node, map, locale) {
    let s = node.nodeValue;
    if (!s) return;
    let out = s;

    out = applyMapBoundarySafe(out, map);
    out = translateWordPaket(out, locale);
    out = translateWordBundle(out, locale);
    out = translateSaveLabel(out, locale);
    out = fixHeadingUppercase(out, locale);

    if (out !== s) node.nodeValue = out;
  }

  function translateAttributes(el, map, locale) {
    ATTRS.forEach(a => {
      const v = el.getAttribute(a);
      if (!v) return;
      let out = v;
      out = applyMapBoundarySafe(out, map);
      out = translateWordPaket(out, locale);
      out = translateWordBundle(out, locale);
      out = translateSaveLabel(out, locale);
      out = fixHeadingUppercase(out, locale);
      if (out !== v) el.setAttribute(a, out);
    });
  }

  function translateIn(root, map, locale) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(n => translateTextNode(n, map, locale));
    root.querySelectorAll('*').forEach(el => translateAttributes(el, map, locale));
  }

  function translateAll() {
    const locale = getLocale();
    const dict = getTargetMap(locale);
    ROOTS.forEach(sel => {
      document.querySelectorAll(sel).forEach(root => {
        translateIn(root, dict, locale);
        if (root.parentElement) translateIn(root.parentElement, dict, locale);
      });
    });
  }

  // ========== Boot & observers (debounced) ==========
  function boot() {
    translateAll();
    let scheduled = false;
    const schedule = () => { if (scheduled) return; scheduled = true; setTimeout(() => { scheduled=false; translateAll(); }, 60); };
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList:true, subtree:true });
    document.addEventListener('shopify:section:load', schedule);
    const langMo = new MutationObserver(muts => { for (const m of muts) if (m.attributeName==='lang'){schedule();break;} });
    langMo.observe(document.documentElement, { attributes:true, attributeFilter:['lang'] });
    ['popstate','pushstate','replacestate'].forEach(evt => window.addEventListener(evt, schedule));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();